import * as vp from 'vopay';

import { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'UI';
const { Box, Select, DateRangePicker } = vp;

import styles from './WebhooksHistory.module.scss';

type WebhooksSearchParams = {
  eventType: string;
  timeRange: [vp.Timestamp, vp.Timestamp];
};

type SearchFormProps = {
  shouldShowEndDateError: boolean;
  setShouldShowEndDateError: (event: boolean) => void;
  hasInvalidEndDate?: boolean;
  eventType: string;
  initialTimeRange: [vp.Timestamp, vp.Timestamp];
  search: (searchParams: WebhooksSearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const WebhooksHistorySearchForm: FC = ({
  hasInvalidEndDate,
  shouldShowEndDateError,
  setShouldShowEndDateError,
  eventType,
  initialTimeRange,
  search,
}: SearchFormProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'WebhooksHistory.SearchForm' });
  const { t: tErrors } = useTranslation('translations', { keyPrefix: 'WebhooksHistory.ErrorMessages' });
  const [currentEventType, setCurrentEventType] = useState<string>(eventType);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>(initialTimeRange);
  const [webhooksHistorySearchError, hasWebhooksHistorySearchError] = useState('');

  const eventTypes = [
    { value: 'all', label: t('Options.EventTypeOptions.all') },
    { value: 'accountverifications', label: t('Options.EventTypeOptions.accountVerifications') },
    { value: 'bankaccount', label: t('Options.EventTypeOptions.bankAccount') },
    { value: 'batchdetail', label: t('Options.EventTypeOptions.batchDetail') },
    { value: 'batchrequest', label: t('Options.EventTypeOptions.batchRequest') },
    { value: 'paylink', label: t('Options.EventTypeOptions.paylink') },
    { value: 'scheduledtransactions', label: t('Options.EventTypeOptions.scheduledTransactions') },
    { value: 'transaction', label: t('Options.EventTypeOptions.transaction') },
    { value: 'transactiongroup', label: t('Options.EventTypeOptions.transactionGroup') },
  ];

  useEffect(() => {
    const ToDate = new Date(timeRange[1]);
    const FromDate = new Date(timeRange[0]);
    const dateDifference = Math.abs(ToDate.getTime() - FromDate.getTime());
    const daysDifference = Math.ceil(dateDifference / (1000 * 60 * 60 * 24));
    if (daysDifference >= 35) {
      hasWebhooksHistorySearchError(tErrors('dateRange'));
    } else {
      hasWebhooksHistorySearchError('');
    }
  }, [timeRange]);

  return (
    <>
      <Box jc="sb" flx="col" width="100%" gap="md">
        <Box gap="sm" wrap="wrap">
          <div className={styles.datePickerContainer}>
            <DateRangePicker
              onChange={(range) => {
                setTimeRange(range);
              }}
              initialTime={initialTimeRange}
              triggerVariant="outlineShadow"
              hasInvalidEndDate={hasInvalidEndDate}
              shouldShowEndDateError={shouldShowEndDateError}
              setShouldShowEndDateError={setShouldShowEndDateError}
            />
          </div>
          <Box width="200px">
            <Select
              version="2"
              placeholder={t('eventPlaceholder')}
              options={eventTypes}
              name="type"
              value={currentEventType}
              onBlur={() => {}}
              onChange={(e) => {
                setCurrentEventType(e.target.value);
              }}
            />
          </Box>
          <Button
            id="search-transaction-history"
            size="small"
            colorScheme="primary"
            disabled={!!webhooksHistorySearchError}
            onClick={() => {
              search({
                eventType: currentEventType,
                timeRange,
              });
            }}
          >
            {t('search')}
          </Button>
        </Box>
        {webhooksHistorySearchError !== '' && (
          <div className={styles.webhookValidationError}>{webhooksHistorySearchError}</div>
        )}
      </Box>
    </>
  );
};

export default WebhooksHistorySearchForm;
